import { useCallback } from 'react';
import { DateUtils } from '../utils/dateUtils';

type FormatOptions = Intl.DateTimeFormatOptions;

const defaultOptions: FormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

export const useDateFormatter = () => {
  /**
   * Formata uma data de forma segura, seguindo as melhores práticas modernas
   * 
   * @param dateInput - String, number, Date, null ou undefined
   * @param options - Opções de formatação do Intl.DateTimeFormat
   * @returns String formatada ou mensagem de erro
   */
  const formatDate = useCallback(
    (
      dateInput: string | number | Date | null | undefined,
      options: FormatOptions = defaultOptions
    ): string => {
      // ✅ PROTEÇÃO: Verificar null/undefined antes de processar
      if (dateInput === null || dateInput === undefined) {
        
        return 'N/A';
      }

      // ✅ PROTEÇÃO: Verificar string vazia
      if (typeof dateInput === 'string' && dateInput.trim() === '') {
        
        return 'N/A';
      }

      try {
        // ✅ MODERNO: Criar Date object de forma defensiva
        const date = new Date(dateInput);
        
        // ✅ PROTEÇÃO: Verificar se a data é válida
        if (isNaN(date.getTime())) {
          
          return 'Data inválida';
        }

        // ✅ MODERNO: Usar Intl.DateTimeFormat para formatação locale-aware
        return new Intl.DateTimeFormat('pt-BR', options).format(date);
        
      } catch (error) {
        
        return 'Erro na data';
      }
    },
    []
  );

  /**
   * Formata data para input type="date" (YYYY-MM-DD)
   * ✅ CORRIGIDO: Usa DateUtils para evitar problemas de timezone
   */
  const formatForInput = useCallback(
    (dateInput: string | number | Date | null | undefined): string => {
      if (dateInput === null || dateInput === undefined) return '';
      
      try {
        // Converter para string se necessário
        const dateString = typeof dateInput === 'string' 
          ? dateInput 
          : new Date(dateInput).toISOString().split('T')[0];
          
        return DateUtils.formatForInput(dateString);
      } catch (error) {
        console.error('Erro ao formatar data para input:', error);
        return '';
      }
    },
    []
  );

  /**
   * Formata data com hora
   */
  const formatDateTime = useCallback(
    (dateInput: string | number | Date | null | undefined): string => {
      return formatDate(dateInput, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    [formatDate]
  );

  /**
   * Formata data de forma compacta (dd/mm/aaaa)
   * ✅ CORRIGIDO: Usa DateUtils para evitar problemas de timezone
   */
  const formatCompact = useCallback(
    (dateInput: string | number | Date | null | undefined): string => {
      if (dateInput === null || dateInput === undefined) return 'N/A';
      if (typeof dateInput === 'string' && dateInput.trim() === '') return 'N/A';
      
      try {
        // Converter para string se necessário
        const dateString = typeof dateInput === 'string' 
          ? dateInput 
          : new Date(dateInput).toISOString().split('T')[0];
          
        return DateUtils.formatDateBR(dateString);
      } catch (error) {
        console.error('Erro ao formatar data compacta:', error);
        return 'Data inválida';
      }
    },
    []
  );

  /**
   * Formata data de forma longa (1 de janeiro de 2024)
   */
  const formatLong = useCallback(
    (dateInput: string | number | Date | null | undefined): string => {
      return formatDate(dateInput, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    [formatDate]
  );

  /**
   * Verifica se uma data é válida
   */
  const isValidDate = useCallback(
    (dateInput: string | number | Date | null | undefined): boolean => {
      if (dateInput === null || dateInput === undefined) return false;
      
      try {
        const date = new Date(dateInput);
        return !isNaN(date.getTime());
      } catch {
        return false;
      }
    },
    []
  );

  return {
    formatDate,
    formatForInput,
    formatDateTime,
    formatCompact,
    formatLong,
    isValidDate,
  };
}; 